import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

import { bibleService } from '../../../services/bibleService';

const BulkImportModal = ({ isOpen, onClose, onImport }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [targetPhase, setTargetPhase] = useState(null);
  const [bibleVersion, setBibleVersion] = useState('KJV');
  const [fileFormat, setFileFormat] = useState('auto');
  const [importProgress, setImportProgress] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importMethod, setImportMethod] = useState('text'); // 'text' or 'file'
  const [textInput, setTextInput] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const fileInputRef = useRef(null);

  // Enhanced Bible book name mappings for long/short reference conversion
  const bookMappings = {
    // Old Testament - Comprehensive mappings
    'gen': 'Genesis', 'ge': 'Genesis', 'genesis': 'Genesis',
    'ex': 'Exodus', 'exo': 'Exodus', 'exodus': 'Exodus', 'exod': 'Exodus',
    'lev': 'Leviticus', 'le': 'Leviticus', 'leviticus': 'Leviticus',
    'num': 'Numbers', 'nu': 'Numbers', 'numbers': 'Numbers',
    'deut': 'Deuteronomy', 'dt': 'Deuteronomy', 'de': 'Deuteronomy', 'deuteronomy': 'Deuteronomy',
    'josh': 'Joshua', 'jos': 'Joshua', 'joshua': 'Joshua',
    'judg': 'Judges', 'jdg': 'Judges', 'judges': 'Judges',
    'ruth': 'Ruth', 'ru': 'Ruth',
    
    // Samuel with Roman numerals and long forms
    '1 sam': '1 Samuel', '1sam': '1 Samuel', '1sa': '1 Samuel', '1 samuel': '1 Samuel',
    'i sam': '1 Samuel', 'i samuel': '1 Samuel', 'first samuel': '1 Samuel', '1st samuel': '1 Samuel',
    '2 sam': '2 Samuel', '2sam': '2 Samuel', '2sa': '2 Samuel', '2 samuel': '2 Samuel',
    'ii sam': '2 Samuel', 'ii samuel': '2 Samuel', 'second samuel': '2 Samuel', '2nd samuel': '2 Samuel',
    
    // Kings with Roman numerals and long forms
    '1 kings': '1 Kings', '1kgs': '1 Kings', '1ki': '1 Kings', '1 ki': '1 Kings',
    'i kings': '1 Kings', 'first kings': '1 Kings', '1st kings': '1 Kings',
    '2 kings': '2 Kings', '2kgs': '2 Kings', '2ki': '2 Kings', '2 ki': '2 Kings',
    'ii kings': '2 Kings', 'second kings': '2 Kings', '2nd kings': '2 Kings',
    
    // Chronicles with Roman numerals and long forms
    '1 chr': '1 Chronicles', '1chron': '1 Chronicles', '1 chronicles': '1 Chronicles',
    'i chr': '1 Chronicles', 'i chronicles': '1 Chronicles', 'first chronicles': '1 Chronicles', '1st chronicles': '1 Chronicles',
    '2 chr': '2 Chronicles', '2chron': '2 Chronicles', '2 chronicles': '2 Chronicles',
    'ii chr': '2 Chronicles', 'ii chronicles': '2 Chronicles', 'second chronicles': '2 Chronicles', '2nd chronicles': '2 Chronicles',
    
    'ezra': 'Ezra', 'ezr': 'Ezra',
    'neh': 'Nehemiah', 'nehemiah': 'Nehemiah',
    'esth': 'Esther', 'est': 'Esther', 'esther': 'Esther',
    'job': 'Job',
    'ps': 'Psalms', 'psa': 'Psalms', 'psalm': 'Psalms', 'psalms': 'Psalms',
    'prov': 'Proverbs', 'pro': 'Proverbs', 'pr': 'Proverbs', 'proverbs': 'Proverbs',
    'eccl': 'Ecclesiastes', 'ecc': 'Ecclesiastes', 'ecclesiastes': 'Ecclesiastes',
    'song': 'Song of Solomon', 'sos': 'Song of Solomon', 'song of solomon': 'Song of Solomon', 'song of songs': 'Song of Songs', 'canticles': 'Song of Solomon',
    'isa': 'Isaiah', 'is': 'Isaiah', 'isaiah': 'Isaiah',
    'jer': 'Jeremiah', 'jeremiah': 'Jeremiah',
    'lam': 'Lamentations', 'lamentations': 'Lamentations',
    'ezek': 'Ezekiel', 'eze': 'Ezekiel', 'ezekiel': 'Ezekiel',
    'dan': 'Daniel', 'daniel': 'Daniel',
    'hos': 'Hosea', 'hosea': 'Hosea',
    'joel': 'Joel',
    'amos': 'Amos',
    'obad': 'Obadiah', 'obadiah': 'Obadiah',
    'jonah': 'Jonah',
    'mic': 'Micah', 'micah': 'Micah',
    'nah': 'Nahum', 'nahum': 'Nahum',
    'hab': 'Habakkuk', 'habakkuk': 'Habakkuk',
    'zeph': 'Zephaniah', 'zep': 'Zephaniah', 'zephaniah': 'Zephaniah',
    'hag': 'Haggai', 'haggai': 'Haggai',
    'zech': 'Zechariah', 'zec': 'Zechariah', 'zechariah': 'Zechariah',
    'mal': 'Malachi', 'malachi': 'Malachi',
    
    // New Testament - Comprehensive mappings
    'matt': 'Matthew', 'mt': 'Matthew', 'mat': 'Matthew', 'matthew': 'Matthew',
    'mark': 'Mark', 'mk': 'Mark', 'mar': 'Mark',
    'luke': 'Luke', 'lk': 'Luke', 'luk': 'Luke',
    'john': 'John', 'jn': 'John', 'joh': 'John',
    'acts': 'Acts', 'act': 'Acts', 'ac': 'Acts', 'the acts': 'Acts', 'acts of the apostles': 'Acts',
    'rom': 'Romans', 'ro': 'Romans', 'romans': 'Romans',
    
    // Corinthians with Roman numerals and long forms
    '1 cor': '1 Corinthians', '1cor': '1 Corinthians', '1co': '1 Corinthians', '1 corinthians': '1 Corinthians',
    'i cor': '1 Corinthians', 'i corinthians': '1 Corinthians', 'first corinthians': '1 Corinthians', '1st corinthians': '1 Corinthians',
    '2 cor': '2 Corinthians', '2cor': '2 Corinthians', '2co': '2 Corinthians', '2 corinthians': '2 Corinthians',
    'ii cor': '2 Corinthians', 'ii corinthians': '2 Corinthians', 'second corinthians': '2 Corinthians', '2nd corinthians': '2 Corinthians',
    
    'gal': 'Galatians', 'ga': 'Galatians', 'galatians': 'Galatians',
    'eph': 'Ephesians', 'ephesians': 'Ephesians',
    'phil': 'Philippians', 'php': 'Philippians', 'philippians': 'Philippians',
    'col': 'Colossians', 'colossians': 'Colossians',
    
    // Thessalonians with Roman numerals and long forms
    '1 thess': '1 Thessalonians', '1thess': '1 Thessalonians', '1th': '1 Thessalonians', '1 thessalonians': '1 Thessalonians',
    'i thess': '1 Thessalonians', 'i thessalonians': '1 Thessalonians', 'first thessalonians': '1 Thessalonians', '1st thessalonians': '1 Thessalonians',
    '2 thess': '2 Thessalonians', '2thess': '2 Thessalonians', '2th': '2 Thessalonians', '2 thessalonians': '2 Thessalonians',
    'ii thess': '2 Thessalonians', 'ii thessalonians': '2 Thessalonians', 'second thessalonians': '2 Thessalonians', '2nd thessalonians': '2 Thessalonians',
    
    // Timothy with Roman numerals and long forms
    '1 tim': '1 Timothy', '1tim': '1 Timothy', '1ti': '1 Timothy', '1 timothy': '1 Timothy',
    'i tim': '1 Timothy', 'i timothy': '1 Timothy', 'first timothy': '1 Timothy', '1st timothy': '1 Timothy',
    '2 tim': '2 Timothy', '2tim': '2 Timothy', '2ti': '2 Timothy', '2 timothy': '2 Timothy',
    'ii tim': '2 Timothy', 'ii timothy': '2 Timothy', 'second timothy': '2 Timothy', '2nd timothy': '2 Timothy',
    
    'titus': 'Titus', 'tit': 'Titus',
    'philem': 'Philemon', 'phm': 'Philemon', 'philemon': 'Philemon',
    'heb': 'Hebrews', 'hebrews': 'Hebrews',
    'jas': 'James', 'james': 'James',
    
    // Peter with Roman numerals and long forms
    '1 pet': '1 Peter', '1pet': '1 Peter', '1pe': '1 Peter', '1 peter': '1 Peter',
    'i pet': '1 Peter', 'i peter': '1 Peter', 'first peter': '1 Peter', '1st peter': '1 Peter',
    '2 pet': '2 Peter', '2pet': '2 Peter', '2pe': '2 Peter', '2 peter': '2 Peter',
    'ii pet': '2 Peter', 'ii peter': '2 Peter', 'second peter': '2 Peter', '2nd peter': '2 Peter',
    
    // John with Roman numerals and long forms
    '1 john': '1 John', '1jn': '1 John', '1jo': '1 John',
    'i john': '1 John', 'first john': '1 John', '1st john': '1 John',
    '2 john': '2 John', '2jn': '2 John', '2jo': '2 John',
    'ii john': '2 John', 'second john': '2 John', '2nd john': '2 John',
    '3 john': '3 John', '3jn': '3 John', '3jo': '3 John',
    'iii john': '3 John', 'third john': '3 John', '3rd john': '3 John',
    
    'jude': 'Jude',
    'rev': 'Revelation', 're': 'Revelation', 'revelation': 'Revelation', 'revelations': 'Revelation', 'the revelation': 'Revelation'
  };

  // Valid Bible verses (simplified validation - in a real app, this would be more comprehensive)
  const validVerses = {
    'Genesis': 50, 'Exodus': 40, 'Leviticus': 27, 'Numbers': 36, 'Deuteronomy': 34,
    'Joshua': 24, 'Judges': 21, 'Ruth': 4, '1 Samuel': 31, '2 Samuel': 24,
    '1 Kings': 22, '2 Kings': 25, '1 Chronicles': 29, '2 Chronicles': 36,
    'Ezra': 10, 'Nehemiah': 13, 'Esther': 10, 'Job': 42, 'Psalms': 150,
    'Proverbs': 31, 'Ecclesiastes': 12, 'Song of Solomon': 8, 'Isaiah': 66,
    'Jeremiah': 52, 'Lamentations': 5, 'Ezekiel': 48, 'Daniel': 12,
    'Hosea': 14, 'Joel': 3, 'Amos': 9, 'Obadiah': 1, 'Jonah': 4,
    'Micah': 7, 'Nahum': 3, 'Habakkuk': 3, 'Zephaniah': 3, 'Haggai': 2,
    'Zechariah': 14, 'Malachi': 4, 'Matthew': 28, 'Mark': 16, 'Luke': 24,
    'John': 21, 'Acts': 28, 'Romans': 16, '1 Corinthians': 16, '2 Corinthians': 13,
    'Galatians': 6, 'Ephesians': 6, 'Philippians': 4, 'Colossians': 4,
    '1 Thessalonians': 5, '2 Thessalonians': 3, '1 Timothy': 6, '2 Timothy': 4,
    'Titus': 3, 'Philemon': 1, 'Hebrews': 13, 'James': 5, '1 Peter': 5,
    '2 Peter': 3, '1 John': 5, '2 John': 1, '3 John': 1, 'Jude': 1, 'Revelation': 22
  };

  const phaseOptions = [
    { value: 0, label: 'Phase 0 (New Verses - Not Started)' },
    { value: 1, label: 'Phase 1 (Daily - Initial Learning)' },
    { value: 2, label: 'Phase 2 (Every 3 Days)' },
    { value: 3, label: 'Phase 3 (Weekly)' },
    { value: 4, label: 'Phase 4 (Bi-Weekly)' },
    { value: 5, label: 'Phase 5 (Monthly)' },
    { value: 6, label: 'Phase 6 (Monthly to Quarterly)' },
    { value: 7, label: 'Phase 7 (Quarterly - Mastered)' }
  ];

  const bibleVersionOptions = [
    { value: 'KJV', label: 'King James Version (KJV)' },
    { value: 'NIV', label: 'New International Version (NIV)' },
    { value: 'ESV', label: 'English Standard Version (ESV)' },
    { value: 'NASB', label: 'New American Standard Bible (NASB)' },
    { value: 'NLT', label: 'New Living Translation (NLT)' },
    { value: 'CSB', label: 'Christian Standard Bible (CSB)' },
    { value: 'NKJV', label: 'New King James Version (NKJV)' },
    { value: 'RSV', label: 'Revised Standard Version (RSV)' }
  ];

  const formatOptions = [
    { value: 'auto', label: 'Auto-detect' },
    { value: 'csv', label: 'CSV File' },
    { value: 'txt', label: 'Text File' },
    { value: 'xlsx', label: 'Excel File' }
  ];

  // Enhanced function to normalize verse references with better Roman numeral and long form support
  const normalizeReference = (reference) => {
    if (!reference) return null;
    
    let ref = reference?.trim()?.toLowerCase();
    
    // Pre-process Roman numerals to Arabic numerals for easier matching
    ref = // normalize whitespace
    // "III " -> "3 "
    // "II " -> "2 "
    // "I " -> "1 "
    ref?.replace(/\bi\s+/g, '1 ')?.replace(/\bii\s+/g, '2 ')?.replace(/\biii\s+/g, '3 ')?.replace(/\s+/g, ' ')?.trim();
    
    // Find matching book name with improved algorithm
    let bookMatch = null;
    let matchedKey = null;
    
    // Try exact match first
    for (const [key, value] of Object.entries(bookMappings)) {
      if (ref?.startsWith(key?.toLowerCase() + ' ') || ref === key?.toLowerCase()) {
        bookMatch = key;
        matchedKey = key;
        break;
      }
    }
    
    // If no exact match, try fuzzy matching for longer book names
    if (!bookMatch) {
      const sortedKeys = Object.keys(bookMappings)?.sort((a, b) => b?.length - a?.length);
      for (const key of sortedKeys) {
        if (ref?.startsWith(key?.toLowerCase())) {
          bookMatch = key;
          matchedKey = key;
          break;
        }
      }
    }
    
    if (!bookMatch) return null;
    
    const fullBookName = bookMappings?.[matchedKey];
    const remainder = ref?.substring(matchedKey?.length)?.trim();
    
    // Parse chapter:verse pattern with enhanced regex
    const chapterVerseMatch = remainder?.match(/^(\d+):(\d+)(?:[-–—](\d+))?$/);
    if (chapterVerseMatch) {
      const chapter = parseInt(chapterVerseMatch?.[1]);
      let verse = parseInt(chapterVerseMatch?.[2]);
      const endVerse = chapterVerseMatch?.[3] ? parseInt(chapterVerseMatch?.[3]) : null;
      
      return {
        book: fullBookName,
        chapter,
        verse,
        endVerse,
        reference: endVerse ? 
          `${fullBookName} ${chapter}:${verse}-${endVerse}` : 
          `${fullBookName} ${chapter}:${verse}`
      };
    }
    
    return null;
  };

  const validateVerse = (normalizedRef) => {
    if (!normalizedRef) return false;
    
    const { book, chapter } = normalizedRef;
    const maxChapter = validVerses?.[book];
    
    if (!maxChapter || chapter > maxChapter || chapter < 1) {
      return false;
    }
    
    // In a real app, you'd validate verse numbers too
    return true;
  };

  const parseVerseReferences = (text) => {
    const lines = text?.split('\n')?.filter(line => line?.trim() !== '');
    const results = [];
    
    // Enhanced regex pattern to handle various verse reference formats including Roman numerals
    const versePattern = /^((?:\d+\s+|I{1,3}\s+|II\s+)?[A-Za-z]+(?:\s+[A-Za-z]+)*)\s+(\d+):(\d+)(?:[-–—](\d+))?$/;
    // Pattern for comma-separated verses: Book Chapter:verse,verse,verse
    const commaPattern = /^((?:\d+\s+|I{1,3}\s+|II\s+)?[A-Za-z]+(?:\s+[A-Za-z]+)*)\s+(\d+):([\d,]+)$/;
    
    // Get available books from the current Bible version
    const bibleBooks = Object.values(bookMappings)?.filter((value, index, self) => self?.indexOf(value) === index);
    const availableBooks = bibleBooks || [];
    
    lines?.forEach((line, index) => {
      const trimmedLine = line?.trim();
      
      // Check for comma-separated verses first
      const commaMatch = trimmedLine?.match(commaPattern);
      if (commaMatch) {
        const [, bookPart, chapterStr, versesStr] = commaMatch;
        let verses = versesStr?.split(',')?.map(v => parseInt(v?.trim()))?.filter(v => !isNaN(v));
        
        // Check if verses are contiguous
        const sortedVerses = [...verses]?.sort((a, b) => a - b);
        let isContiguous = true;
        for (let i = 1; i < sortedVerses?.length; i++) {
          if (sortedVerses?.[i] !== sortedVerses?.[i - 1] + 1) {
            isContiguous = false;
            break;
          }
        }
        
        if (!isContiguous) {
          results?.push({
            reference: trimmedLine,
            isValid: false,
            error: `Verses must be contiguous (e.g., 1,2,3 or 1-3). Non-contiguous verses like "1,3" are not supported (line ${index + 1})`
          });
          return;
        }
        
        // Convert to range format and process
        const startVerse = sortedVerses?.[0];
        const endVerse = sortedVerses?.[sortedVerses?.length - 1];
        const rangeReference = `${bookPart} ${chapterStr}:${startVerse}-${endVerse}`;
        
        // Process as a normal range
        const normalizedMatch = rangeReference?.match(versePattern);
        if (normalizedMatch) {
          const [, normalizedBook, normalizedChapter, normalizedStart, normalizedEnd] = normalizedMatch;
          processVerseMatch(normalizedBook, normalizedChapter, normalizedStart, normalizedEnd, rangeReference, index);
        }
        return;
      }
      
      // Normal verse pattern processing
      const match = trimmedLine?.match(versePattern);
      if (!match) {
        results?.push({
          reference: trimmedLine,
          isValid: false,
          error: `Invalid verse format. Expected format: "Book Chapter:Verse" or "Book Chapter:Verse-Verse" (line ${index + 1})`
        });
        return;
      }

      const [, bookPart, chapterStr, startVerseStr, endVerseStr] = match;
      processVerseMatch(bookPart, chapterStr, startVerseStr, endVerseStr, trimmedLine, index);
    });
    
    // Helper function to process verse matches
    function processVerseMatch(bookPart, chapterStr, startVerseStr, endVerseStr, trimmedLine, index) {
      // Clean and normalize book name with Roman numeral conversion
      let normalizedBookInput = bookPart?.trim()?.toLowerCase();
      
      // Convert Roman numerals to Arabic numerals
      normalizedBookInput = // normalize whitespace
      // "III " -> "3 "
      // "II " -> "2 "
      // "I " -> "1 "
      normalizedBookInput?.replace(/\bi\s+/g, '1 ')?.replace(/\bii\s+/g, '2 ')?.replace(/\biii\s+/g, '3 ')?.replace(/\s+/g, ' ')?.trim();

      // Find matching book name using the enhanced bookMappings
      let matchedBookName = null;
      
      // Try direct lookup in our comprehensive mapping
      if (bookMappings?.[normalizedBookInput]) {
        matchedBookName = bookMappings?.[normalizedBookInput];
      } else {
        // Try fuzzy matching with available books
        const foundBook = availableBooks?.find(book => 
          book?.toLowerCase() === normalizedBookInput ||
          book?.toLowerCase()?.includes(normalizedBookInput) ||
          normalizedBookInput?.includes(book?.toLowerCase()?.substring(0, 3))
        );
        if (foundBook) {
          matchedBookName = foundBook;
        }
      }

      if (!matchedBookName) {
        results?.push({
          reference: trimmedLine,
          isValid: false,
          error: `Book "${bookPart}" not recognized. Try using full book names like "1 Corinthians" or "I Corinthians" (line ${index + 1})`
        });
        return;
      }

      const chapter = parseInt(chapterStr);
      const startVerse = parseInt(startVerseStr);
      const endVerse = endVerseStr ? parseInt(endVerseStr) : startVerse;

      // Enhanced validation
      if (chapter <= 0) {
        results?.push({
          reference: trimmedLine,
          isValid: false,
          error: `Invalid chapter number: ${chapter} (line ${index + 1})`
        });
        return;
      }

      if (startVerse <= 0) {
        results?.push({
          reference: trimmedLine,
          isValid: false,
          error: `Invalid verse number: ${startVerse} (line ${index + 1})`
        });
        return;
      }

      if (endVerse < startVerse) {
        results?.push({
          reference: trimmedLine,
          isValid: false,
          error: `End verse ${endVerse} cannot be less than start verse ${startVerse} (line ${index + 1})`
        });
        return;
      }

      // Create verse references for the range
      const verseReferences = [];
      for (let verse = startVerse; verse <= endVerse; verse++) {
        verseReferences?.push({
          book: matchedBookName,
          chapter: chapter,
          verse: verse,
          reference: `${matchedBookName} ${chapter}:${verse}`
        });
      }

      results?.push({
        reference: trimmedLine,
        isValid: true,
        verseReferences: verseReferences,
        book: matchedBookName,
        chapter: chapter,
        startVerse: startVerse,
        endVerse: endVerse
      });
    }

    return results;
  };

  const parseTextInput = (text) => {
    const lines = text?.split('\n')?.filter(line => line?.trim());
    let verses = [];
    
    lines?.forEach((line, index) => {
      const trimmedLine = line?.trim();
      
      if (trimmedLine) {
        verses?.push({
          line: index + 1,
          originalReference: trimmedLine,
          text: '',
          version: bibleVersion,
          notes: ''
        });
      }
    });
    
    return verses;
  };

  const parseFile = async (file) => {
    const text = await file?.text();
    const extension = file?.name?.split('.')?.pop()?.toLowerCase();
    let verses = [];

    try {
      if (extension === 'csv') {
        verses = parseCSV(text);
      } else if (extension === 'txt') {
        verses = parseTXT(text);
      } else if (extension === 'xlsx' || extension === 'xls') {
        // For Excel files, you'd need a library like xlsx
        // For now, show error message
        throw new Error('Excel file parsing requires additional setup. Please convert to CSV or TXT format.');
      } else {
        // Auto-detect format
        if (text?.includes(',') && text?.includes('\n')) {
          verses = parseCSV(text);
        } else {
          verses = parseTXT(text);
        }
      }
    } catch (error) {
      throw new Error(`Failed to parse file: ${error.message}`);
    }

    return verses;
  };

  const parseCSV = (text) => {
    const lines = text?.split('\n')?.filter(line => line?.trim());
    let verses = [];
    
    lines?.forEach((line, index) => {
      const parts = line?.split(',')?.map(part => part?.trim()?.replace(/"/g, ''));
      if (parts?.length >= 2) {
        const reference = parts?.[0];
        const text = parts?.[1];
        const version = parts?.[2] || bibleVersion;
        const notes = parts?.[3] || '';
        
        verses?.push({
          line: index + 1,
          originalReference: reference,
          text,
          version,
          notes
        });
      }
    });
    
    return verses;
  };

  const parseTXT = (text) => {
    const lines = text?.split('\n')?.filter(line => line?.trim());
    let verses = [];
    
    lines?.forEach((line, index) => {
      const trimmedLine = line?.trim();
      
      // Try to find reference pattern at start of line
      const referenceMatch = trimmedLine?.match(/^([^-]+?)\s*[-–—]\s*(.+)$/);
      if (referenceMatch) {
        const reference = referenceMatch?.[1]?.trim();
        const text = referenceMatch?.[2]?.trim();
        
        verses?.push({
          line: index + 1,
          originalReference: reference,
          text,
          version: bibleVersion,
          notes: ''
        });
      } else {
        // Fallback: treat entire line as reference if it looks like one
        const possibleRef = trimmedLine?.match(/^([A-Za-z0-9\s]+\d+:\d+)/);
        if (possibleRef) {
          verses?.push({
            line: index + 1,
            originalReference: trimmedLine,
            text: '',
            version: bibleVersion,
            notes: ''
          });
        }
      }
    });
    
    return verses;
  };

  const processVerses = (verses) => {
    return verses?.map(verse => {
      const normalizedRef = normalizeReference(verse?.originalReference);
      const isValid = validateVerse(normalizedRef);
      
      return {
        ...verse,
        normalizedReference: normalizedRef,
        isValid,
        errors: isValid ? [] : ['Invalid or unrecognized Bible reference']
      };
    });
  };

  const handleTextInputChange = (e) => {
    setTextInput(e?.target?.value);
    setPreviewData(null); // Clear preview when text changes
  };

  const handleValidateText = async () => {
    if (!textInput?.trim()) return;

    setIsValidating(true);
    setPreviewData(null);

    try {
      let verses = parseTextInput(textInput);
      const processedVerses = processVerses(verses);
      setPreviewData(processedVerses);
    } catch (error) {
      alert(`Error processing text input: ${error?.message}`);
    } finally {
      setIsValidating(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setIsProcessing(true);
    setPreviewData(null);

    try {
      let verses = await parseFile(file);
      const processedVerses = processVerses(verses);
      setPreviewData(processedVerses);
    } catch (error) {
      alert(`Error processing file: ${error?.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImport = async () => {
    if (!previewData) return;

    // Validate that phase is selected (not null/undefined)
    if (targetPhase === null || targetPhase === undefined) {
      alert('Please select a target phase before importing.');
      return;
    }

    setIsProcessing(true);

    const validVerses = previewData?.filter(verse => verse?.isValid);
    const invalidVerses = previewData?.filter(verse => !verse?.isValid);

    // Process verse ranges as single references (not expanded)
    const processedVerses = [];
    const failedVerses = [...invalidVerses];

    // Parallel processing: fetch all verses simultaneously
    const versePromises = validVerses?.map(async (verse) => {
      try {
        const verseStart = verse?.normalizedReference?.verse || verse?.normalizedReference?.startVerse;
        const verseEnd = verse?.normalizedReference?.endVerse || verseStart;
        const bookName = verse?.normalizedReference?.book;
        const chapterNumber = verse?.normalizedReference?.chapter;
        const version = (verse?.version || bibleVersion)?.toLowerCase();

        // Calculate verse count
        const verseCount = verseEnd - verseStart + 1;

        // Build reference string
        const reference = verseCount > 1 
          ? `${bookName} ${chapterNumber}:${verseStart}-${verseEnd}`
          : `${bookName} ${chapterNumber}:${verseStart}`;

        console.log(`Fetching verse range: ${reference} (${version})`);

        // Fetch verse text from Bible database for the range
        const { data: verseTexts, error: fetchError } = await bibleService?.getVerseRangeText(
          bookName,
          chapterNumber,
          verseStart,
          verseEnd,
          version
        );

        if (fetchError || !verseTexts || verseTexts?.length === 0) {
          console.error(`Failed to fetch verse: ${reference}`, fetchError);
          return {
            success: false,
            verse: {
              ...verse,
              isValid: false,
              error: fetchError?.message || 'Could not fetch verse text from Bible database'
            }
          };
        }

        // Combine verse texts with verse numbers (except first verse)
        let combinedText = '';
        verseTexts?.forEach((verseText, index) => {
          if (index === 0) {
            // First verse: no verse number prefix
            combinedText = verseText?.text;
          } else {
            // Subsequent verses: include verse number
            combinedText += `  ${verseText?.verseNumber}  ${verseText?.text}`;
          }
        });

        // Return successful verse data
        return {
          success: true,
          verse: {
            reference: reference,
            book: bookName,
            chapter: chapterNumber,
            verse_start: verseStart,
            verse_end: verseEnd,
            verse_count: verseCount,
            text: combinedText,
            bible_version: version,
            notes: verse?.notes || '',
            current_phase: targetPhase,
            tags: [],
            review_count: 0
          }
        };
      } catch (err) {
        console.error('Error processing verse:', err);
        return {
          success: false,
          verse: {
            ...verse,
            isValid: false,
            error: err?.message || 'Error processing verse'
          }
        };
      }
    });

    // Wait for all promises to settle (both successful and failed)
    const results = await Promise.allSettled(versePromises);

    // Process results
    results?.forEach((result) => {
      if (result?.status === 'fulfilled') {
        if (result?.value?.success) {
          processedVerses?.push(result?.value?.verse);
        } else {
          failedVerses?.push(result?.value?.verse);
        }
      } else {
        // Promise rejected
        failedVerses?.push({
          isValid: false,
          error: result?.reason?.message || 'Promise rejected'
        });
      }
    });

    // Create the properly formatted results object
    const resultsObject = {
      verses: processedVerses,
      imported: processedVerses?.map((verse, index) => ({
        id: Date.now() + index,
        reference: verse?.reference,
        text: verse?.text,
        version: verse?.bible_version?.toLowerCase(),
        progress: 0,
        tags: [],
        notes: verse?.notes || '',
        dateAdded: new Date()?.toISOString()?.split('T')?.[0],
        lastStudied: null,
        isInLibrary: true,
        hasNotes: Boolean(verse?.notes),
        hasLinks: false,
        phase: targetPhase,
        verseStart: verse?.verse_start,
        verseEnd: verse?.verse_end,
        verseCount: verse?.verse_count,
        isPassage: verse?.verse_count > 1
      })),
      failed: failedVerses,
      total: previewData?.length,
      phase: targetPhase,
      format: importMethod
    };

    setImportProgress(resultsObject);
    
    // Pass results to parent component
    onImport(resultsObject);
    
    setIsProcessing(false);
  };

  const resetModal = () => {
    setSelectedFile(null);
    setPreviewData(null);
    setImportProgress(null);
    setIsProcessing(false);
    setTextInput('');
    setImportMethod('text');
    setBibleVersion('KJV');
    setTargetPhase(0);
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-card border border-border rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-gradient-to-r from-primary/10 via-primary/5 to-transparent">
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              Bulk Import Verses
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Add multiple verses at once to your library
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Import Progress Results */}
          {importProgress ? (
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full mx-auto mb-4">
                  <Icon name="CheckCircle" size={32} className="text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">Import Complete!</h3>
                <p className="text-muted-foreground">
                  Successfully imported {importProgress?.imported?.length} of {importProgress?.total} verses
                  into <span className="font-medium">{phaseOptions?.find(p => p?.value === importProgress?.phase)?.label}</span>
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="CheckCircle" size={16} className="text-green-600 dark:text-green-400" />
                    <span className="font-medium text-green-800 dark:text-green-200">Successfully Imported</span>
                  </div>
                  <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                    {importProgress?.imported?.length}
                  </p>
                </div>

                {importProgress?.failed?.length > 0 && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="XCircle" size={16} className="text-red-600 dark:text-red-400" />
                      <span className="font-medium text-red-800 dark:text-red-200">Failed to Import</span>
                    </div>
                    <p className="text-2xl font-bold text-red-900 dark:text-red-100">
                      {importProgress?.failed?.length}
                    </p>
                  </div>
                )}
              </div>

              {importProgress?.failed?.length > 0 && (
                <div className="bg-muted rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-foreground mb-3">Failed Verses:</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {importProgress?.failed?.map((verse, index) => (
                      <div key={index} className="flex items-start space-x-3 text-sm">
                        <span className="text-muted-foreground">Line {verse?.line}:</span>
                        <span className="text-foreground font-mono">{verse?.originalReference}</span>
                        <span className="text-red-600 dark:text-red-400">{verse?.errors?.join(', ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={handleClose}>Close</Button>
              </div>
            </div>
          ) : (
            <div className="p-6 space-y-5">

              {/* Step 1: Choose Method */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  <h3 className="text-sm font-semibold text-foreground">Choose Import Method</h3>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={() => setImportMethod('text')}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors duration-200 text-sm ${
                      importMethod === 'text' ?'bg-primary text-primary-foreground border-primary' :'bg-background text-foreground border-border hover:bg-muted'
                    }`}
                  >
                    <Icon name="Type" size={15} />
                    <span>Paste References</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setImportMethod('file')}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors duration-200 text-sm ${
                      importMethod === 'file' ?'bg-primary text-primary-foreground border-primary' :'bg-background text-foreground border-border hover:bg-muted'
                    }`}
                  >
                    <Icon name="Upload" size={15} />
                    <span>Upload File</span>
                  </button>
                </div>
              </div>

              {/* Step 2: Enter Verses */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  <h3 className="text-sm font-semibold text-foreground">
                    {importMethod === 'text' ? 'Enter Verse References' : 'Upload Your File'}
                  </h3>
                </div>

                {/* Format hint */}
                <div className="bg-muted/50 border border-border/60 rounded-lg px-4 py-3 mb-3 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">Accepted formats: </span>
                  John 3:16 &nbsp;·&nbsp; Rom 8:28 &nbsp;·&nbsp; 1 Cor 13:4-7 &nbsp;·&nbsp; I Corinthians 13:4-7
                  {importMethod === 'file' && <span> &nbsp;·&nbsp; CSV / TXT / XLSX</span>}
                </div>

                {/* Text Input */}
                {importMethod === 'text' && (
                  <div>
                    <textarea
                      value={textInput}
                      onChange={handleTextInputChange}
                      placeholder="John 3:16&#10;Romans 8:28&#10;Philippians 4:13&#10;1 Cor 13:4-7"
                      className="w-full h-40 px-3 py-2 border border-border rounded-lg bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                    />
                    <div className="flex justify-end mt-2">
                      <Button
                        variant={textInput?.trim() ? "default" : "outline"}
                        size="sm"
                        onClick={handleValidateText}
                        disabled={!textInput?.trim() || isValidating}
                        iconName={isValidating ? "Loader2" : "CheckCircle"}
                        iconPosition="left"
                      >
                        {isValidating ? 'Validating...' : 'Validate Verses'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* File Upload */}
                {importMethod === 'file' && (
                  <div>
                    <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.txt,.xlsx,.xls"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      {selectedFile ? (
                        <div className="flex items-center justify-center space-x-3">
                          <Icon name="FileText" size={24} className="text-primary" />
                          <div>
                            <p className="font-medium text-foreground">{selectedFile?.name}</p>
                            <p className="text-sm text-muted-foreground">{(selectedFile?.size / 1024)?.toFixed(1)} KB</p>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => fileInputRef?.current?.click()}>
                            Change File
                          </Button>
                        </div>
                      ) : (
                        <div>
                          <Icon name="Upload" size={40} className="text-muted-foreground mx-auto mb-3" />
                          <p className="text-foreground font-medium mb-1">Choose a file to import</p>
                          <p className="text-sm text-muted-foreground mb-4">CSV, TXT, XLS, XLSX</p>
                          <Button variant="outline" onClick={() => fileInputRef?.current?.click()} iconName="Upload" iconPosition="left">
                            Select File
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-foreground mb-1">File Format</label>
                      <Select options={formatOptions} value={fileFormat} onChange={setFileFormat} placeholder="Select format..." />
                    </div>
                  </div>
                )}
              </div>

              {/* Step 3: Configure */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  <h3 className="text-sm font-semibold text-foreground">Configure Import Settings</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Memorization Phase <span className="text-red-500">*</span>
                    </label>
                    <Select
                      options={phaseOptions}
                      value={targetPhase}
                      onChange={setTargetPhase}
                      placeholder="Select phase (required)..."
                    />
                    <p className="text-xs text-muted-foreground mt-1">Phase 0 = not yet started</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Bible Version</label>
                    <Select
                      options={bibleVersionOptions}
                      value={bibleVersion}
                      onChange={setBibleVersion}
                      placeholder="Select version..."
                    />
                  </div>
                </div>
              </div>

              {/* Preview */}
              {previewData && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">4</span>
                    <h3 className="text-sm font-semibold text-foreground">Review & Import</h3>
                  </div>
                  <div className="border border-border rounded-lg overflow-hidden">
                    <div className="bg-muted px-4 py-2 border-b border-border flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">{previewData?.length} verses found</span>
                      <div className="flex items-center space-x-4 text-xs">
                        <span className="text-green-600 dark:text-green-400 font-medium">
                          ✓ {previewData?.filter(v => v?.isValid)?.length} valid
                        </span>
                        {previewData?.filter(v => !v?.isValid)?.length > 0 && (
                          <span className="text-red-600 dark:text-red-400 font-medium">
                            ✗ {previewData?.filter(v => !v?.isValid)?.length} invalid
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="max-h-52 overflow-y-auto">
                      {previewData?.slice(0, 10)?.map((verse, index) => (
                        <div
                          key={index}
                          className={`px-4 py-2.5 border-b border-border last:border-b-0 ${
                            verse?.isValid ? 'bg-background' : 'bg-red-50 dark:bg-red-900/10'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2 min-w-0">
                              <Icon
                                name={verse?.isValid ? "CheckCircle" : "XCircle"}
                                size={14}
                                className={verse?.isValid ? "text-green-600 dark:text-green-400 flex-shrink-0" : "text-red-600 dark:text-red-400 flex-shrink-0"}
                              />
                              <span className="font-medium text-sm text-foreground truncate">
                                {verse?.normalizedReference?.reference || verse?.originalReference}
                              </span>
                            </div>
                            {!verse?.isValid && (
                              <span className="text-xs text-red-600 dark:text-red-400 flex-shrink-0">
                                {verse?.errors?.[0]}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                      {previewData?.length > 10 && (
                        <div className="px-4 py-2 text-center text-sm text-muted-foreground">
                          … and {previewData?.length - 10} more
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-2 border-t border-border">
                <Button variant="outline" onClick={handleClose}>Cancel</Button>
                <Button
                  variant="default"
                  onClick={handleImport}
                  disabled={!previewData || previewData?.filter(v => v?.isValid)?.length === 0 || isProcessing || targetPhase === null || targetPhase === undefined}
                  iconName={isProcessing ? "Loader2" : "Upload"}
                  iconPosition="left"
                >
                  {isProcessing ? 'Importing...' : `Import ${previewData?.filter(v => v?.isValid)?.length || 0} Verses`}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;